import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import cx from "classnames";

import {
  initialiseEnquireLabs,
  loadConfirmedCheckout,
} from "redux/actions/checkout";
import constants from "api/constants";

import ThankYouBlock from "./checkoutConfirmation/ThankYouBlock";
import DeliveryDetailsBlock from "./checkoutConfirmation/DeliveryDetailsBlock";
import PaymentDetailsBlock from "./checkoutConfirmation/PaymentDetailsBlock";
import OrderSummaryBlock from "./checkoutConfirmation/OrderSummaryBlock";
import ReferralBlock from "./checkoutConfirmation/ReferralBlock";
import RewardsSignupBlock from "./checkoutConfirmation/RewardsSignupBlock";
import EnquireModal from "./testPage/EnquireModal";
import { getConfirmationOrder } from "./checkoutConfirmation/confirmationOrder";

import styles from "./checkoutConfirmation/CheckoutConfirmation.module.scss";

const CheckoutConfirmation = () => {
  const dispatch = useDispatch();
  const { checkoutToken } = useParams();
  const { cart, completedCheckout, contactDetails } = useSelector((s) => ({
    ...s.auth,
    ...s.checkout,
  }));

  const [isEnquireModalVisible, setIsEnquireModalVisible] = useState(false);
  const [customerEnrolled, setCustomerEnrolled] = useState(false);

  useEffect(() => {
    if (completedCheckout && contactDetails && window.EnquireLabs) {
      setTimeout(() => {
        setIsEnquireModalVisible(true);
      }, 2000);

      setTimeout(() => {
        dispatch(initialiseEnquireLabs(completedCheckout, contactDetails));
      }, 2250);
    }
  }, [completedCheckout, contactDetails, dispatch]);

  useEffect(() => {
    if (completedCheckout && completedCheckout.token === checkoutToken) {
      return;
    }

    dispatch(loadConfirmedCheckout(checkoutToken));
  }, [checkoutToken, completedCheckout, dispatch]);

  const order = useMemo(
    () => (completedCheckout ? getConfirmationOrder(completedCheckout) : null),
    [completedCheckout],
  );

  if (!completedCheckout || !order) {
    return null;
  }

  /**
   * `cart` is only populated by the cart-fetch handlers — completing a checkout
   * doesn't repopulate it — so on a reload or a direct link to the confirmation
   * URL it is null and a `cart.country_code` gate can never pass. The completed
   * checkout is the only source that survives, so read the country from there
   * and keep the cart as a fallback for the uninterrupted hop.
   */
  const country =
    completedCheckout.subscription?.shipping_address?.country_code ??
    completedCheckout.order?.shipping_address?.country_code ??
    completedCheckout.subscription?.country_code ??
    cart?.country_code;

  // `RewardsSignupBlock` reads `loyalty.points_value` unguarded, so the block
  // has to be gated on the reward actually being there — "enrolled is not true"
  // is also satisfied by loyalty being absent altogether.
  const rewardPoints = Number(completedCheckout.loyalty?.points_value);

  const showRewards =
    completedCheckout.loyalty?.enrolled === false &&
    !customerEnrolled &&
    Number.isFinite(rewardPoints) &&
    rewardPoints > 0 &&
    country === "GB";

  return (
    <div className={cx("gkit-container", styles.page)}>
      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          <ThankYouBlock
            className={styles.thanks}
            orderNumber={order.orderNumber}
          />

          <DeliveryDetailsBlock
            className={styles.delivery}
            shippingMethod={order.shippingMethod}
          />

          <PaymentDetailsBlock
            amountPaid={order.amountPaid}
            billingAddress={order.billingAddress}
            card={order.card}
            className={styles.payment}
          />

          {/*
            Not in the confirmation design — kept because it's a live GB
            loyalty enrolment with its own analytics event. It sits in the wide
            column because its internal layout was built for that width.
          */}
          {showRewards && (
            <RewardsSignupBlock
              className={styles.rewards}
              completedCheckout={completedCheckout}
              setCustomerEnrolled={setCustomerEnrolled}
            />
          )}
        </div>

        <div className={styles.asideColumn}>
          <OrderSummaryBlock
            className={styles.summary}
            discount={order.discount}
            lineItems={order.lineItems}
            total={order.total}
          />

          {/* The only route off this page, so it sits with the order. */}
          <a
            className={cx(
              "gkit-btn-lg gkit-primary gkit-w-full",
              styles.continueShopping,
            )}
            href={constants.shopifyURL}
          >
            Continue shopping
          </a>

          <ReferralBlock className={styles.referral} />
        </div>
      </div>

      <EnquireModal
        isVisible={isEnquireModalVisible}
        onDismissed={() => setIsEnquireModalVisible(false)}
      />
    </div>
  );
};

export default CheckoutConfirmation;
